import * as React from 'react';
import { createRef, useEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import { getKeyboardFocusableElements } from 'utilities/getKeyboardFocusableElements';

export interface PopoverComponentProps {
	closePopover: () => void;
	triggerElement: HTMLElement;
	children: React.ReactNode;
}

export const PopoverComponent = ({ closePopover, triggerElement, children }: PopoverComponentProps): JSX.Element => {
	const popoverRef = createRef<HTMLDivElement>();

	const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
	const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
	const { styles, attributes } = usePopper(triggerElement, popperElement, {
		modifiers: [{ name: 'arrow', options: { element: arrowElement } }],
	});

	const popperStyles = styles.popper;

	const [arrowPosition, setArrowPosition] = useState<string | null>(null);

	const popover = (
		<div
			ref={setPopperElement}
			className={`popover bs-popover-${arrowPosition}`}
			role="tooltip"
			{...attributes.popper}
			style={{ width: 'max-content', ...popperStyles }}
		>
			<div ref={setArrowElement} className="arrow" style={styles.arrow} />
			<div ref={popoverRef} className="popover-body">
				{children}
			</div>
		</div>
	);

	const closeOnEscape = (e: KeyboardEvent) => {
		if (e.code === 'Escape') {
			closePopover();
		}
	};

	const closeOnClick = (e: MouseEvent) => {
		if (e.target && (e.target as HTMLElement).closest('.popover') === null) {
			closePopover();
		}
	};

	useEffect(() => {
		document.body.addEventListener('click', closeOnClick, true);
		document.body.addEventListener('keydown', closeOnEscape);
		return () => {
			document.body.removeEventListener('click', closeOnClick, true);
			document.body.removeEventListener('keydown', closeOnEscape);
		};
	});

	useEffect(() => {
		if (attributes.popper !== undefined) {
			setArrowPosition(attributes.popper['data-popper-placement']);
		}
	}, [attributes.popper]);

	useEffect(() => {
		popoverRef.current?.focus();
	});

	useEffect(() => {
		if (!popoverRef.current) return;

		const focusables = getKeyboardFocusableElements(popoverRef.current);

		if (focusables.length === 0) return;

		const first = focusables[0];
		const last = focusables[focusables.length - 1];

		first.addEventListener('keydown', (e: KeyboardEvent) => {
			if (e.code === 'Tab' && e.shiftKey) {
				e.preventDefault();
				last.focus();
			}
		});

		last.addEventListener('keydown', (e: KeyboardEvent) => {
			if (e.code === 'Tab' && !e.shiftKey) {
				e.preventDefault();
				first.focus();
			}
		});
	});

	return popover;
};
